/**
 * Client-side image compression utility
 * Reduces file size and converts iPhone HEIC/HEIF to JPEG
 */

import heic2any from 'heic2any';

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeMB?: number;
  outputFormat?: 'jpeg' | 'png' | 'webp';
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.8,
  maxSizeMB: 2,
  outputFormat: 'jpeg',
};

const HEIC_CONVERSION_TIMEOUT_MS = 30000;
const IMAGE_COMPRESSION_TIMEOUT_MS = 30000;
const FILE_READER_TIMEOUT_MS = 30000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out. Please try a smaller image or retry.`));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });
}

/**
 * Compresses an image file to reduce size and convert format if needed
 */
export async function compressImage(file: File, options: CompressionOptions = {}): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    // Handle HEIC/HEIF files first
    let processedFile = file;

    if (
      file.type.toLowerCase().includes('heic') ||
      file.type.toLowerCase().includes('heif') ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')
    ) {
      console.log('Converting HEIC file to JPEG...');

      try {
        const converted = await withTimeout(
          heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.8,
          }) as Promise<Blob | Blob[]>,
          HEIC_CONVERSION_TIMEOUT_MS,
          'HEIC conversion'
        );
        const convertedBlob = Array.isArray(converted) ? converted[0] : converted;

        processedFile = new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });

        console.log(
          `HEIC converted: ${(file.size / (1024 * 1024)).toFixed(2)}MB → ${(convertedBlob.size / (1024 * 1024)).toFixed(2)}MB`
        );
      } catch (heicError) {
        console.error('HEIC conversion failed:', heicError);
        throw new Error('Failed to convert HEIC/HEIF file to JPEG');
      }
    }

    // Create canvas for image manipulation
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    // Create image element
    const img = new Image();

    return new Promise((resolve, reject) => {
      let objectUrl: string | null = null;
      let settled = false;
      const timeoutId = setTimeout(() => {
        settleWithError(
          new Error('Image compression timed out. Please try a smaller image or retry.')
        );
      }, IMAGE_COMPRESSION_TIMEOUT_MS);

      const cleanup = () => {
        clearTimeout(timeoutId);
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
        img.onload = null;
        img.onerror = null;
        img.src = '';
      };

      const settleWithFile = (compressedFile: File) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(compressedFile);
      };

      function settleWithError(error: Error) {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      }

      img.onload = () => {
        try {
          // Calculate new dimensions maintaining aspect ratio
          let { width, height } = img;

          if (width > opts.maxWidth || height > opts.maxHeight) {
            const aspectRatio = width / height;

            if (width > height) {
              width = Math.min(width, opts.maxWidth);
              height = width / aspectRatio;
            } else {
              height = Math.min(height, opts.maxHeight);
              width = height * aspectRatio;
            }
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw and compress image
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // Check if we need further compression
              const sizeMB = blob.size / (1024 * 1024);

              if (sizeMB > opts.maxSizeMB && opts.quality > 0.1) {
                // Reduce quality and try again
                const newOptions = { ...opts, quality: opts.quality * 0.8 };
                compressImage(processedFile, newOptions)
                  .then(settleWithFile)
                  .catch(settleWithError);
                return;
              }

              // Create new file with compressed data
              const compressedFile = new File([blob], processedFile.name, {
                type: `image/${opts.outputFormat}`,
                lastModified: Date.now(),
              });

              console.log(
                `Image compressed: ${(processedFile.size / (1024 * 1024)).toFixed(2)}MB → ${(blob.size / (1024 * 1024)).toFixed(2)}MB`
              );
              settleWithFile(compressedFile);
            },
            `image/${opts.outputFormat}`,
            opts.quality
          );
        } catch (error) {
          settleWithError(error instanceof Error ? error : new Error('Image compression failed'));
        }
      };

      img.onerror = () => {
        settleWithError(new Error('Failed to load image for compression'));
      };

      // Create object URL for the image
      objectUrl = URL.createObjectURL(processedFile);
      img.src = objectUrl;
    });
  } catch (error) {
    console.error('Image compression failed:', error);
    // Return original file if compression fails
    return file;
  }
}

/**
 * Converts file to data URL for preview
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      reader.abort();
      reject(new Error('Reading image timed out. Please try a smaller image or retry.'));
    }, FILE_READER_TIMEOUT_MS);

    const cleanup = () => {
      clearTimeout(timeoutId);
      reader.onload = null;
      reader.onerror = null;
      reader.onabort = null;
    };

    reader.onload = (e) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('FileReader error'));
    };
    reader.onabort = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('File reading was cancelled'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Validates file type and size
 */
export function validateImageFile(
  file: File,
  maxSizeMB: number = 50
): { valid: boolean; error?: string } {
  // Check file type
  const allowedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/tiff',
    'image/heic',
    'image/heif',
  ];

  const allowedExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.bmp',
    '.tiff',
    '.heic',
    '.heif',
  ];
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

  if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
    return {
      valid: false,
      error: `File type not supported. Allowed types: ${allowedExtensions.join(', ')}`,
    };
  }

  // Check file size
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return {
      valid: false,
      error: `File size too large (${fileSizeMB.toFixed(1)}MB). Maximum size is ${maxSizeMB}MB.`,
    };
  }

  return { valid: true };
}
